import { CSSProperties } from 'react';
import styled, { useTheme } from 'styled-components';
import { Divider, getSpacingToken, getToken, SpacingToken, Stack, StackItem, Typography } from '@optimus-web/core';
import { ChartData, LabelFormatter, ValueFormatter } from '../../types';
import { getDataVizColor } from '../colors';
import { HighlightScore } from './HighlightScore';

interface LegendContentProps {
    payload: ChartData[];
    vertical?: boolean;
    showValues?: boolean;
    valueFormatter?: ValueFormatter;
    labelFormatter?: LabelFormatter;
    spacing?: SpacingToken;
}

export const LabelBadge = styled.div<{ $color?: string; isLine?: boolean }>`
    width: ${getSpacingToken('100')}px;
    height: ${({ isLine }) => getSpacingToken(isLine ? '50' : '100')}px;
    border-radius: ${getToken('borderRadius25')}px;
    background-color: ${({ $color }) => $color};
`;

export const LEGEND_WRAPPER_STYLES: CSSProperties = {
    position: 'static',
    width: 'auto',
    height: 'auto',
    left: 'auto',
    bottom: 'auto',
};

export const LegendContent = ({
    payload,
    vertical,
    showValues,
    valueFormatter,
    labelFormatter,
    spacing = '100',
}: LegendContentProps) => {
    const theme = useTheme();

    const labelTypographyPreset = vertical ? 'bodyMedium' : 'bodySmall';

    return (
        <Stack wrap spacing={spacing} vertical={vertical} data-test-legend-content>
            {payload.map(({ value, name, color, id }, index) => (
                <Stack key={id} vertical spacing="25" data-test-chart-node-id={id} data-test-chart-node-value={value}>
                    {!vertical && showValues ? (
                        <HighlightScore value={valueFormatter ? valueFormatter(value, name) : value} />
                    ) : null}
                    <Stack itemAlignment="center" spacing="100" distribution="space-between">
                        <StackItem>
                            <Stack itemAlignment="center" spacing={vertical ? '100' : '50'}>
                                <LabelBadge $color={getDataVizColor(index, color)({ theme })} />
                                <Typography textStyle={labelTypographyPreset} key={`item-${value}`} as="div">
                                    {labelFormatter ? labelFormatter(name) : name}
                                </Typography>
                            </Stack>
                        </StackItem>
                        {vertical && showValues ? (
                            <Typography textStyle={labelTypographyPreset} as="div">
                                {valueFormatter ? valueFormatter(value, name) : value}
                            </Typography>
                        ) : null}
                    </Stack>
                    {vertical && index !== payload.length - 1 ? <Divider /> : null}
                </Stack>
            ))}
        </Stack>
    );
};
